/**
 * dashboard-adapter.ts — dashboard 域「真 API DTO → 页面消费形状」塑形
 *
 * 接真改造：真实后端 GET /api/v1/dashboard/overview 返回 snake_case 富聚合 DTO
 * （DashboardOverviewResponse，见 packages/shared/src/schemas/dashboard.ts），而 DashboardPage
 * （经 useDashboard hook）消费 camelCase 派生形状。本 adapter 做字段对照 + 单位换算 + 行为埋点
 * 占位降级，让页面接真、数据变真、设计还原不变。
 *
 * 关键口径与缺字段处理：
 * - 金额单位：真 DTO 用 minor units（分）string；页面图表 / KPI 用 major units number → 一律
 *   /100（minorToMajor，币种符号由 dto.currency 决定，当前 currency=USD，不再写死 EUR）。
 * - 收入趋势 / 拆解（revenue_trend）：渠道闭集仅 IAP/STRIPE（张培裁决 ADM-DASH-S002 不含 PayPal）。
 * - KPI 标量（kpi.*）：累计收入 / ARPPU 为法币 minor→major number；付费用户为整数；DAU 为行为埋点
 *   占位（PENDING_INSTRUMENTATION，页面灰显「埋点接入中」，不显假数字）；被动退款率 value+delta_pp。
 * - revenue_by_app（App 营收占比）：直接透 app_id/app_name + revenue minor→major + share_pct。
 *   注意 DTO 仅有 app_id/app_name，无 icon/color → 页面侧用 useApps() 按 app_id join 取图标。
 * - top_books（热门书籍）：title / author / category（name_i18n 按 zh→en→第一个取展示名）/ cover_url
 *   （真图 CDN URL）/ readers（窗口内活跃阅读 UV）/ payers / paid_chapters /
 *   revenue（**Coin 整数 1:1，不 ÷100**；此为章节解锁 Coin 口径、非法币充值额，与 KPI USD 区分）。
 * - alerts（风险与告警）：pending_count + items（level→tone：CRITICAL→neg/WARN→warn/INFO→info；
 *   type→图标名；title/detail/occurred_at 透传，occurred_at 原值，无相对时间 helper 时直接展示）。
 * - 🔸 行为类（dau / retention / funnel / dau_series）：范围内无 sink 就绪 App → 真 DTO 为
 *   PENDING_INSTRUMENTATION 占位，adapter 降级为空数组 + behavior.pending，页面渲染「埋点接入中」；
 *   就绪时为真值（只聚合 behavioral_coverage.ready_app_ids，页面注明「仅覆盖同意分析采集的用户」
 *   并列出未接入 App）。就绪但当期无数据 = 真实空（pending=false），页面显「当期无数据」而非接入中。
 *   refund30d 同理：真 DTO 无「逐日退款序列」（KPI 仅给当期退款率）→ 暂空数组。
 */

import { type DashboardOverviewResponse, isPendingInstrumentation } from '@novel-admin/shared';
import type { BreakdownRow, FunnelRow, RetentionRow, Series } from '~/data/mock/types';

/** minor units（分）string → major units number；非法/空 → 0。币种符号由 dto.currency 决定（不再写死 EUR）。 */
function minorToMajor(minor: string | null | undefined): number {
  if (!minor) return 0;
  const n = Number(minor);
  return Number.isFinite(n) ? n / 100 : 0;
}

/**
 * Coin minor-units string → 整数 number（**Coin 1:1，不 ÷100**；非法/空 → 0）。
 * Coin 是整数虚拟币、无「百分之一 Coin」子单位，其 minor-units 仅为 bigint 存储表示，展示 1:1（钱包
 * coin_balance / Paywall coin 均 1:1）。**不可复用 minorToMajor**——那是法币美分→元的 ÷100，套到 Coin
 * 会 100× 偏小（top_books.revenue 是章节解锁 Coin 口径、非法币充值额，dev-docs 05 §2.3 / A0）。
 */
function coinToInt(coin: string | null | undefined): number {
  if (!coin) return 0;
  const n = Number(coin);
  return Number.isFinite(n) ? n : 0;
}

/** category.name_i18n 取展示名：优先 zh → en → 第一个 value（恒有，dev-docs #118）*/
function pickI18nName(nameI18n: Record<string, string>): string {
  return nameI18n.zh ?? nameI18n.en ?? Object.values(nameI18n)[0] ?? '';
}

/** alerts level → 设计 token tone（颜色档位 var(--neg/warn/info)）*/
const ALERT_LEVEL_TONE: Record<'INFO' | 'WARN' | 'CRITICAL', 'neg' | 'warn' | 'info'> = {
  CRITICAL: 'neg',
  WARN: 'warn',
  INFO: 'info',
};

/**
 * alerts type → 图标名（本页 Icons 查表用，限 dashboard 告警类型）。
 * 退款率/支付异常 → IconAlert；风控/IP 异常 → IconShield；内容/翻译缺失 → IconBook；其它兜底 IconAlert。
 */
const ALERT_TYPE_ICON: Record<string, AlertIconName> = {
  REFUND_RATE: 'IconAlert',
  PAYMENT_ANOMALY: 'IconAlert',
  RISK: 'IconShield',
  ABUSE: 'IconShield',
  CONTENT: 'IconBook',
  TRANSLATION: 'IconBook',
};

/** 漏斗 step 英文枚举 → 原型中文标签（与 dashboard.jsx FUNNEL 文案对齐）*/
const FUNNEL_STEP_LABEL: Record<string, string> = {
  APP_OPEN: '应用打开',
  HOME_VIEW: '进入书城',
  NOVEL_VIEW: '打开书籍详情',
  READING_START: '开始阅读',
  PAID_UNLOCK: '解锁付费章节',
  PURCHASE: '完成充值',
};

/** 告警卡用到的图标名（限本页 Icons 查表，避免 any 索引）*/
export type AlertIconName = 'IconAlert' | 'IconShield' | 'IconBook';

/** KPI 单标量（金额 / 计数通用）：当期值 + 环比百分比；DAU 占位时 pending=true */
export type DashboardKpiView = { value: number; deltaPct: number | null; pending?: boolean };

/** 累计收入 / 付费用户 / DAU / ARPPU / 被动退款率（页面 KPI 5 卡）*/
export type DashboardKpiSet = {
  totalRevenue: DashboardKpiView;
  payingUsers: DashboardKpiView;
  /** 🔸 行为埋点未就绪 → pending=true，页面灰显「埋点接入中」（不显假数字）*/
  dau: DashboardKpiView;
  /** ARPPU：付费用户=0 时 value=null（页面显「—」）；currency 决定符号 */
  arppu: { value: number | null; currency: string; deltaPct: number | null };
  /** 被动退款率：百分比 value + 百分点 delta */
  passiveRefundRate: { value: number | null; deltaPp: number | null };
};

/** App 营收占比单行（页面 donut + list；icon/color 由页面侧 useApps() join app_id 补）*/
export type DashboardRevenueByAppRow = {
  appId: string;
  appName: string;
  /** 法币 major units number */
  revenue: number;
  sharePct: number;
};

/** 热门书籍单行（页面 TOP 表；revenue 为 Coin 口径 major number，非法币）*/
export type DashboardTopBookRow = {
  novelId: string;
  title: string;
  author: string | null;
  categoryName: string;
  coverUrl: string;
  /** 🔸 行为埋点未就绪 → null，页面灰显「—」*/
  readers: number | null;
  payers: number;
  paidChapters: number;
  /** 章节解锁 Coin 整数（**1:1，非 ÷100**；Coin 口径、非法币充值额）*/
  revenue: number;
};

/** 风险与告警单条（页面卡片渲染）*/
export type DashboardAlertItem = {
  tone: 'neg' | 'warn' | 'info';
  icon: AlertIconName;
  title: string;
  /** 副标题（DTO detail）*/
  sub: string;
  /** 发生时间（DTO occurred_at 原值，无相对时间 helper 时直接展示）*/
  time: string;
  /**
   * 关联资源引用（DTO alerts.items[].ref）：domain（如 USER / CONTENT）+ resource_id。
   * 页面据此把告警条目跳转到对应处理域（已知 domain → 路由；未知 domain → 不可点，不杜撰路由）。
   */
  ref: { domain: string; resourceId: string };
};

export type DashboardAlertsView = {
  pendingCount: number;
  items: DashboardAlertItem[];
};

/** 行为指标就绪与覆盖（页面据此区分「埋点接入中」与「当期无数据」，并注明覆盖口径）*/
export type DashboardBehaviorView = {
  /** 范围内无 sink 就绪 App（四项全 PENDING）*/
  pending: boolean;
  /** 未接入埋点、不计入行为指标的 App */
  missingAppIds: string[];
};

/** 页面 useDashboard 消费的完整形状 */
export type DashboardViewData = {
  /** 币种（dto.currency，当前 'USD'）— 决定 KPI / 营收金额符号 */
  currency: string;
  financialCoverage?: {
    usd_ready: boolean;
    data_as_of: string | null;
    income_window: { start: string; end: string } | null;
  };
  /** KPI 5 卡真值 */
  kpi: DashboardKpiSet;
  behavior: DashboardBehaviorView;
  /** App 营收占比真值（仅 scope=all 有值）*/
  revenueByApp: DashboardRevenueByAppRow[];
  /** 热门书籍真值 */
  topBooks: DashboardTopBookRow[];
  /** 风险与告警真值 */
  alerts: DashboardAlertsView;
  // ↓ 原有时序 / 拆解形状（页面图表消费，保持不动）
  revenue30d: Series;
  users30d: Series;
  dau30d: Series;
  refund30d: Series;
  revenueBreakdown: BreakdownRow[];
  retention: RetentionRow[];
  funnel: FunnelRow[];
  /** 整体转化率（百分比）；埋点未就绪 / 后端未给时 null（页面占位）*/
  funnelOverallRate: number | null;
};

/**
 * 真 overview DTO → 页面 DashboardViewData。
 * 行为埋点占位字段一律降级空数组 / pending 标记（页面空态渲染，不显假数据）。
 */
export function adaptDashboardOverview(dto: DashboardOverviewResponse): DashboardViewData {
  // revenueBreakdown：逐日双渠道 minor→major（渠道闭集 = IAP / STRIPE，ADM-DASH-S002 不含 PayPal）
  const revenueBreakdown: BreakdownRow[] = dto.revenue_trend.points.map((p) => ({
    date: p.date,
    iap: minorToMajor(p.iap),
    stripe: minorToMajor(p.stripe),
  }));

  // revenue30d：逐日双渠道求和（单序列总收入趋势）
  const revenue30d: Series = revenueBreakdown.map((p) => ({
    date: p.date,
    value: Math.round(p.iap + p.stripe),
  }));

  // users30d：付费用户逐日（真值 ✅）
  const users30d: Series = dto.user_trend.paying_users_series.map((p) => ({
    date: p.date,
    value: p.value,
  }));

  // 🔸 dau30d：dau_series 埋点占位 → 空数组；有真值时映射
  const dau30d: Series = isPendingInstrumentation(dto.user_trend.dau_series)
    ? []
    : dto.user_trend.dau_series.map((p) => ({ date: p.date, value: p.value }));

  // refund30d：真 DTO 当前无逐日退款序列 → 空数组（KPI 仅给当期退款率）
  const refund30d: Series = [];

  // 🔸 retention：埋点占位 → 空数组；有真值时映射 cohorts → RetentionRow
  const retention: RetentionRow[] = isPendingInstrumentation(dto.retention)
    ? []
    : dto.retention.cohorts.map((c) => ({ cohort: c.cohort_start, days: c.days }));

  // 🔸 funnel：埋点占位 → 空数组；有真值时映射 step→中文 label + value
  const funnel: FunnelRow[] = isPendingInstrumentation(dto.funnel)
    ? []
    : dto.funnel.map((f) => ({ label: FUNNEL_STEP_LABEL[f.step] ?? f.step, value: f.users }));

  // KPI 5 卡：金额 minor→major number；DAU 行为埋点占位 → pending；ARPPU value 可空
  const dauPending = isPendingInstrumentation(dto.kpi.dau);
  const kpi: DashboardKpiSet = {
    totalRevenue: {
      value: minorToMajor(String(dto.kpi.total_revenue.value)),
      deltaPct: dto.kpi.total_revenue.delta_pct,
      pending: !dto.financial_coverage?.usd_ready || dto.kpi.total_revenue.value === null,
    },
    payingUsers: {
      value: Number(dto.kpi.paying_users.value),
      deltaPct: dto.kpi.paying_users.delta_pct,
    },
    dau: dauPending
      ? { value: 0, deltaPct: 0, pending: true }
      : {
          value: Number((dto.kpi.dau as { value: string | number }).value),
          deltaPct: (dto.kpi.dau as { delta_pct: number }).delta_pct,
        },
    arppu: {
      // payers=0 时后端给 value=null → 透传 null（页面显「—」）
      value: dto.kpi.arppu.value === null ? null : minorToMajor(dto.kpi.arppu.value),
      currency: dto.kpi.arppu.currency,
      deltaPct: dto.kpi.arppu.delta_pct,
    },
    passiveRefundRate: {
      value: dto.kpi.passive_refund_rate.value,
      deltaPp: dto.kpi.passive_refund_rate.delta_pp,
    },
  };

  // App 营收占比：revenue minor→major；icon/color 由页面侧 useApps() join app_id 补
  const revenueByApp: DashboardRevenueByAppRow[] = dto.revenue_by_app.map((a) => ({
    appId: a.app_id,
    appName: a.app_name,
    revenue: minorToMajor(a.revenue),
    sharePct: a.share_pct,
  }));

  // 热门书籍：title / category 多语言名 / cover_url 真图 / readers 窗口活跃 UV / revenue Coin 1:1
  const topBooks: DashboardTopBookRow[] = dto.top_books.map((b) => ({
    novelId: b.novel_id,
    title: b.title,
    author: b.author,
    categoryName: pickI18nName(b.category.name_i18n),
    coverUrl: b.cover_url,
    // ✅ 窗口内活跃阅读 UV（契约恒 integer）。view model 仍留 null 位：部署序是 Web→Server，
    // 新 Web 撞旧 admin-api 时该字段可能仍是 null，页面据此灰显「—」而不是渲染成 0
    readers: b.readers ?? null,
    payers: b.payers,
    paidChapters: b.paid_chapters,
    // Coin 整数 1:1（**不 ÷100**）——章节解锁 Coin 口径、非法币，不可复用 minorToMajor
    revenue: coinToInt(b.revenue),
  }));

  // 风险与告警：level→tone、type→icon、occurred_at 原值透传（无相对时间 helper）
  const alerts: DashboardAlertsView = {
    pendingCount: dto.alerts.pending_count,
    items: dto.alerts.items.map((it) => ({
      tone: ALERT_LEVEL_TONE[it.level],
      icon: ALERT_TYPE_ICON[it.type] ?? 'IconAlert',
      title: it.title,
      sub: it.detail,
      time: it.occurred_at,
      ref: { domain: it.ref.domain, resourceId: it.ref.resource_id },
    })),
  };

  return {
    currency: dto.currency,
    financialCoverage: dto.financial_coverage,
    kpi,
    behavior: {
      pending: dto.data_freshness.behavioral !== 'realtime',
      // 旧 admin-api（部署序 Web→Server）无该字段 → 视为无已知缺失 App
      missingAppIds: dto.behavioral_coverage?.missing_app_ids ?? [],
    },
    revenueByApp,
    topBooks,
    alerts,
    revenue30d,
    users30d,
    dau30d,
    refund30d,
    revenueBreakdown,
    retention,
    funnel,
    funnelOverallRate: dto.funnel_overall_rate,
  };
}
